import stringify from 'fast-json-stable-stringify';
import { hash } from 'immutable';

import type {
  AmountSourceConditionType,
  AmountType,
  PayeeMatchingType,
  QCSAmountSourceConditionType,
  QCSTransactionRuleRequestType,
  RemoteTransactionRuleType,
  SubmitPayload,
  TransactionRuleConditionStatusTypeKeys,
  TransactionRuleType,
} from './transactionRulesTypes';

// TODO: In future phases, the conditions we check will grow
export const checkRuleConditions = (formPayload: SubmitPayload): string | undefined => {
  const { enabled } = formPayload;
  const error =
    !enabled.payeeSource && !enabled.amountSource && !enabled.accountSource
      ? 'Add IF conditions'
      : !enabled.payeeTarget &&
          !enabled.coaTarget &&
          !enabled.businessCoaTarget &&
          !enabled.excludedTarget &&
          !enabled.reviewedTarget &&
          !enabled.memoTarget &&
          !enabled.tagsTarget
        ? 'Add THEN conditions'
        : undefined;
  return error;
};

// TODO: In future phases, the conditions we hash will grow to include all source conditions
export const getRuleHash = (rule: TransactionRuleType): string => {
  const hashedConditions = { ...rule.payeeSource, ...rule.accountSource, ...rule.amountSource };
  return hash(stringify(hashedConditions)).toString();
};

export const getDuplicateTransactionRuleId = (rules: TransactionRuleType[], rule: TransactionRuleType) => {
  const hashKey = getRuleHash(rule);
  const duplicate = rules.find((rule) => (rule.hashKey || getRuleHash(rule)) === hashKey);
  // note: if the id's are the same, its an edit so not a duplicate
  return duplicate && rule.id !== duplicate.id ? duplicate.id : undefined;
};

// TODO: In future phases, the conditions we search will grow
export const getFilteredTransactionRules = (rules: TransactionRuleType[], searchValue: string, categories: any) => {
  return rules
    .filter((rule) => {
      // search for matching payeeTarget
      if (rule.payeeTarget?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) {
        return true;
      }

      // search for matchning payeeSource
      if (
        rule.payeeSource?.payeeMatchingCriteria.some((criterion: PayeeMatchingType) =>
          criterion.payeeName?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
        )
      ) {
        return true;
      }

      // search for matching categoryTarget
      if (categories.get(rule.coaTarget?.id)?.name?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) {
        return true;
      }

      return false;
    })
    .sort((a, b) => (a.modifiedAt! > b.modifiedAt! ? -1 : a.modifiedAt! < b.modifiedAt! ? 1 : 0));
};

/**
 * Converts amount based on whether it is an expense or income.
 * QCS expects all amounts to be negative for expenses and positive for incomes.
 */
export const convertAmountByType = (amount: number | undefined, amountType: AmountType | undefined): number => {
  if (amount === undefined) {
    return 0;
  }

  if (amountType === 'EXPENSE' && amount > 0) {
    return -amount;
  } else if (amountType === 'INCOME' && amount < 0) {
    return Math.abs(amount);
  }

  return amount;
};

export type TxnRuleTrackingSource = 'settingsCreate' | 'settingsEdit' | 'txnPayee' | 'txnCategory' | 'txnDetails';

export type TxnRuleTrackingContext = {
  fromTxn?: boolean;
  editField?: TransactionRuleConditionStatusTypeKeys;
  source?: TxnRuleTrackingSource;
};

export type TxnRuleTrackingProps = {
  matchOn: string[];
  updateTxn: string[];
  nameType: 'quicken' | 'statement' | undefined;
  applyTo: 'includePast' | 'excludePast';
  source: TxnRuleTrackingSource;
};

export const computeTxnRuleTrackingProps = (
  rule: Partial<TransactionRuleType> | undefined,
  context?: TxnRuleTrackingContext,
): TxnRuleTrackingProps => {
  const matchOn: string[] = [];
  if (rule?.payeeSource) matchOn.push('payee');
  if (rule?.amountSource) matchOn.push('amount');
  if (rule?.accountSource) matchOn.push('account');

  const updateTxn: string[] = [];
  if (rule?.payeeTarget) updateTxn.push('payee');
  if (rule?.coaTarget || rule?.businessCoaTarget) updateTxn.push('category');
  if (typeof rule?.reviewedTarget === 'boolean') updateTxn.push('reviewed');
  if (rule?.memoTarget) updateTxn.push('notes');
  if (Array.isArray(rule?.tagsTarget) && rule?.tagsTarget?.length) updateTxn.push('tags');
  if (rule?.excludedTarget) updateTxn.push('exclude');

  const nameType =
    rule?.payeeSource?.payeeType === 'INFERRED' ? 'quicken' : rule?.payeeSource ? 'statement' : undefined;

  const applyTo = rule?.applyToExistingTransactions ? 'includePast' : 'excludePast';

  let source: TxnRuleTrackingSource;
  if (context?.source) {
    source = context.source;
  } else if (context?.fromTxn) {
    source = context?.editField === 'coaTarget' ? 'txnCategory' : 'txnPayee';
  } else {
    source = rule?.id ? 'settingsEdit' : 'settingsCreate';
  }

  return {
    matchOn,
    updateTxn,
    nameType,
    applyTo,
    source,
  };
};

export const transformQCSResponseToClientData = (resources: RemoteTransactionRuleType[]) => {
  const payload = resources.map((resource): TransactionRuleType => {
    return {
      // entity
      id: resource.id,
      clientId: resource.clientId,
      createdAt: resource.createdAt,
      modifiedAt: resource.modifiedAt,
      hashKey: resource.hashKey,
      // source
      payeeSource: resource.payeeSourceCondition,
      // This conversion adds the amountType to the amountSource.
      // QCS does not return the amountType, so we need to infer it from the amount1.
      amountSource: resource.amountSourceCondition
        ? {
            ...resource.amountSourceCondition,
            amountType:
              typeof resource.amountSourceCondition.amount1 === 'number'
                ? resource.amountSourceCondition.amount1 > 0
                  ? 'INCOME'
                  : 'EXPENSE'
                : 'EXPENSE',
          }
        : undefined,
      coaSource: resource.coaSourceConditions,
      accountSource: resource.accountSourceCondition,
      usageSource: resource.usageSourceType,
      // target
      payeeTarget: resource.payeeTarget,
      coaTarget: resource.coaTargetCondition,
      businessCoaTarget: resource.businessCoaTargetCondition,
      useDefaultAccountUsage: resource.useDefaultAccountUsage,
      tagsTarget: resource.tagsTargetIds,
      memoTarget: resource.memoTarget,
      excludedTarget:
        typeof resource.isExcludedFromReportsTarget === 'boolean' ||
        typeof resource.isExcludedFromF2STarget === 'boolean'
          ? {
              isExcludedFromReports: resource.isExcludedFromReportsTarget,
              isExcludedFromF2S: resource.isExcludedFromF2STarget,
            }
          : undefined,
      reviewedTarget: resource.isReviewedTarget,
      businessTargetId: resource.businessTargetId,
      // other
      applyToExistingTransactions: resource.applyToExistingTransactions,
      //linking
      prevTransactionRuleId: resource.prevTransactionRuleId,
      nextTransactionRuleId: resource.nextTransactionRuleId,
    };
  });

  return payload;
};

const transformAmountSourceToQCS = (
  amountSource: AmountSourceConditionType | undefined,
): QCSAmountSourceConditionType | undefined => {
  if (!amountSource || amountSource.amountMatchingOperator === 'ANY_AMOUNT') {
    return undefined;
  }

  if (amountSource.amountMatchingOperator === 'BETWEEN') {
    return {
      amountMatchingOperator: amountSource.amountMatchingOperator,
      amount1: amountSource.amount1,
      amount2: amountSource.amount2,
    };
  }

  return {
    amountMatchingOperator: amountSource.amountMatchingOperator,
    amount1: amountSource.amount1,
  };
};

export const transformClientDataToQCSResponse = (resource: TransactionRuleType): QCSTransactionRuleRequestType => {
  const amountSourceCondition = transformAmountSourceToQCS(resource.amountSource);
  const payload = {
    // entity
    id: resource.id,
    clientId: resource.clientId,
    createdAt: resource.createdAt,
    modifiedAt: resource.modifiedAt,
    hashKey: resource.hashKey,
    // source
    payeeSourceCondition: resource.payeeSource,
    ...(amountSourceCondition ? { amountSourceCondition } : {}),
    coaSourceConditions: resource.coaSource,
    accountSourceCondition: resource.accountSource,
    usageSourceType: resource.usageSource,
    // target
    payeeTarget: resource.payeeTarget,
    coaTargetCondition: resource.coaTarget,
    businessCoaTargetCondition: resource.businessCoaTarget,
    useDefaultAccountUsage: resource.useDefaultAccountUsage,
    tagsTargetIds: resource.tagsTarget,
    memoTarget: resource.memoTarget,
    isExcludedFromReportsTarget:
      resource.excludedTarget === undefined ? undefined : resource.excludedTarget.isExcludedFromReports,
    isExcludedFromF2STarget:
      resource.excludedTarget === undefined ? undefined : resource.excludedTarget.isExcludedFromF2S,
    isReviewedTarget: resource.reviewedTarget,
    businessTargetId: resource.businessTargetId,
    // other
    applyToExistingTransactions: resource.applyToExistingTransactions,
    //linking
    prevTransactionRuleId: resource.prevTransactionRuleId,
    nextTransactionRuleId: resource.nextTransactionRuleId,
  };

  return payload;
};
